import { useCallback, useState } from 'react';
import { consumeQuota } from '../services/quota';
import { QuotaError, condenserCv } from '../services/claude';
import { proposeAbonnement, messageRefusSansOffre } from '../utils/quotaRefusal';
import { usePaywallStore } from '../store/paywallStore';
import { shareCvPdf, type CvMeta } from '../services/pdf';
import { genererCvPdfUnePage } from '../services/cvPdfUnePage';
import type { GeneratedCv } from '../types';

/**
 * Export PDF du CV. Ordre voulu : génère localement → gate serveur (consomme le quota,
 * refuse si dépassé) → partage UNIQUEMENT si le gate accepte.
 * Ainsi un échec de génération ne coûte jamais un export, et le quota reste enforced
 * côté serveur avant que l'utilisateur reçoive son fichier.
 */
export function useCvExport() {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaFeature, setQuotaFeature] = useState<'export' | null>(null);
  // Contenu réellement exporté quand il a fallu condenser ou tronquer pour tenir sur une page :
  // l'écran l'affiche à la place de l'aperçu, pour que l'aperçu soit le PDF.
  const [ajustement, setAjustement] = useState<{ cv: GeneratedCv; condense: boolean; tronque: boolean } | null>(null);

  const exportCv = useCallback(async (cv: GeneratedCv, meta: CvMeta): Promise<boolean> => {
    setExporting(true);
    setError(null);
    setQuotaFeature(null);
    try {
      // Local et gratuit : une page A4, toujours. La condensation serveur ne coûte aucun quota.
      const pdf = await genererCvPdfUnePage(cv, meta, async (c) => {
        try {
          return await condenserCv(c, meta.outputLanguage);
        } catch {
          return null;
        }
      });
      setAjustement(pdf.condense || pdf.tronque ? { cv: pdf.cv, condense: pdf.condense, tronque: pdf.tronque } : null);
      await consumeQuota('export'); // gate serveur (lève QuotaError si 402)
      await shareCvPdf(pdf.uri); // seulement si le gate a accepté
      return true;
    } catch (e) {
      if (e instanceof QuotaError) {
        setQuotaFeature('export'); // PDF non délivré
        if (proposeAbonnement(e.plan)) {
          usePaywallStore.getState().open('export', e.reason);
        } else {
          setError(messageRefusSansOffre(e.reason));
        }
      } else {
        setError(e instanceof Error ? e.message : 'L’export a échoué.');
      }
      return false;
    } finally {
      setExporting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setQuotaFeature(null);
  }, []);

  return { exportCv, exporting, error, quotaFeature, ajustement, reset };
}
